'use client';

import { useEffect } from 'react';
import { drawBrandCube } from '@/lib/brand-cube';

// Animated favicon: redraws the Votely brand cube to a hidden canvas at
// ~12fps and patches every favicon `<link>` href with the resulting data
// URL on every frame. All the geometry lives in lib/brand-cube so the
// favicon, the /dev/cube-opacity tuning page, and the /brand asset guide
// stay in lockstep.

// Brand standard: 70% cell opacity, all-faces (volumetric) rendering. This
// value reads correctly against both the brand light bg (#eef1ef) and the
// dark surface (#2a4173), so no per-context branching is needed. Keep this
// in sync with the default in lib/brand-cube.ts if either is changed.
const CELL_ALPHA = 0.70;

export default function AnimatedFavicon() {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const SIZE = 64;
    const canvas = document.createElement('canvas');
    canvas.width = SIZE;
    canvas.height = SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Take over the favicon. Browsers pick between multiple `<link rel=
    // "icon">` based on sizes + type, and Next.js's metadata block renders
    // several (SVG, .ico, 16, 32) which we don't control directly — React's
    // head reconciler re-injects them if we just delete them. So instead of
    // fighting React, we PATCH every existing icon link to point at our
    // data URL on every frame: doesn't matter which one the browser picks,
    // they all carry the same animated payload.
    //
    // We also normalize `type` to image/png on the SVG entry so browsers
    // don't reject the PNG data URL as a content-type mismatch. We
    // intentionally LEAVE `rel="apple-touch-icon"` and `rel="mask-icon"`
    // alone — those are home-screen / pinned-tab assets that browsers don't
    // re-fetch during a session, so animating them buys nothing.
    const iconSelector = 'link[rel="icon"], link[rel="shortcut icon"]';
    const normalizeLinks = () => {
      const links = document.querySelectorAll<HTMLLinkElement>(iconSelector);
      links.forEach((el) => {
        el.type = 'image/png';
        el.removeAttribute('sizes');
      });
      if (links.length === 0) {
        const link = document.createElement('link');
        link.rel = 'icon';
        link.type = 'image/png';
        document.head.appendChild(link);
      }
      return document.querySelectorAll<HTMLLinkElement>(iconSelector);
    };
    normalizeLinks();

    const applyHref = (href: string) => {
      let links = document.querySelectorAll<HTMLLinkElement>(iconSelector);
      if (links.length === 0) links = normalizeLinks();
      links.forEach((el) => {
        if (el.getAttribute('type') !== 'image/png') el.type = 'image/png';
        if (el.hasAttribute('sizes')) el.removeAttribute('sizes');
        el.href = href;
      });
    };

    // Start the cube at a gentle corner view (~28°) rather than head-on,
    // so the resting frame shows the front 3x3 grid mostly square-on with
    // a sliver of the right side peeking through. Browsers that refuse to
    // animate favicons get this as their static frame.
    const INITIAL_THETA = (Math.PI / 180) * 28;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      ctx.clearRect(0, 0, SIZE, SIZE);
      drawBrandCube(ctx, { size: SIZE, theta: INITIAL_THETA, cellAlpha: CELL_ALPHA });
      applyHref(canvas.toDataURL('image/png'));
      return;
    }

    let theta = INITIAL_THETA;
    let lastT = performance.now();
    let timerId: number | null = null;
    let firstFrame = true;

    const tick = () => {
      timerId = window.setTimeout(tick, 1000 / 12);
      if (!firstFrame && document.visibilityState !== 'visible') return;
      firstFrame = false;
      const now = performance.now();
      const dt = (now - lastT) / 1000;
      lastT = now;
      // ~36 deg/sec — full revolution every 10s
      theta += dt * (Math.PI / 5);
      ctx.clearRect(0, 0, SIZE, SIZE);
      drawBrandCube(ctx, { size: SIZE, theta, cellAlpha: CELL_ALPHA });
      applyHref(canvas.toDataURL('image/png'));
    };
    tick();

    return () => {
      if (timerId !== null) window.clearTimeout(timerId);
    };
  }, []);

  return null;
}
